import {useNavigate, useLocation, useParams} from 'react-router-dom';
import { Formik, Field, Form, ErrorMessage, useFormikContext } from 'formik';
import axios from 'axios';
import {useState, useEffect, useRef, useContext} from 'react';
import { AuthContext } from '../contexts/AuthContext';
import {
  StoreSingleDatePicker, 
  TimePicker, 
  CloseButton, 
  titleCaseText, 
  EditMenu, 
  Loading, 
  SimplePicksDisplay, 
  SimpleSearchPreview,
  ConfirmationPopup,
  CouponForm
} from './utility/Utility';
import _ from 'lodash';
import { ActionDescription } from './Onboarding';

export const GiveawaysHeader = (props) => {
  const {dynamicName} = props;
  const location = useLocation();
  const {id} = useParams();
  const navigate = useNavigate();

  const goBack = (e, link) => {
    if(e.currentTarget.classList.contains('update-promotion__breadcrumb--past')) {
      navigate(link);
    }
  }

  return (
    <section className="update-giveaway__header">
      <h1 className="update-promotion__heading">{`Giveaways > ${dynamicName}`}</h1>
      <div className="update-promotion__breadcrumbs">
        <div onClick={(e) => goBack(e, `/app/giveaways/${id}`)} className={`update-promotion__breadcrumb${!location.pathname.includes('rewards') && !location.pathname.includes('review') ? ' update-promotion__breadcrumb--active' : ' update-promotion__breadcrumb--past'}`}>
          <div className={`update-promotion__breadcrumb-number ${!location.pathname.includes('rewards') && !location.pathname.includes('review') ? ' update-promotion__breadcrumb-number--active' : ''}`}>
            1
          </div>
          <div className="update-promotion__breadcrumb-title">
            SEGMENT
          </div>
        </div>
        <div className="update-promotion__breadcrumb-trail"></div>
        <div onClick={(e) => goBack(e, `/app/giveaways/${id}/rewards`)} className={`update-promotion__breadcrumb${location.pathname.includes('rewards') ? ' update-promotion__breadcrumb--active' : location.pathname.includes('review') ? ' update-promotion__breadcrumb--past' : ''}`}>
          <div className={`update-promotion__breadcrumb-number ${location.pathname.includes('rewards') ? ' update-promotion__breadcrumb-number--active' : ''}`}>
            2
          </div>
          <div className="update-promotion__breadcrumb-title">
            REWARDS
          </div>
        </div>
        <div className="update-promotion__breadcrumb-trail"></div>
        <div className={`update-promotion__breadcrumb${location.pathname.includes('review') ? ' update-promotion__breadcrumb--active' : ''}`}>
        <div className={`update-promotion__breadcrumb-number ${location.pathname.includes('review') ? ' update-promotion__breadcrumb-number--active' : ''}`}>
            3
          </div>
          <div className="update-promotion__breadcrumb-title">
            REVIEW
          </div>
        </div>
      </div>
    </section>
  )
}

export const GiveawaysInfo = (props) => {
  const {handleSignOut, getJwtToken} = useContext(AuthContext);
  const {giveaway, setGiveaway, dynamicName, setDynamicName, tutorial, setTutorial} = props;
  const {id} = useParams();
  const navigate = useNavigate();
  const [filters, setFilters] = useState(giveaway.filters || []);
  const [errors, setErrors] = useState(false);
  const [segment, setSegment] = useState(0);
  const [prevFilters, setPrevFilters] = useState([]);
  const [fetchingSeg, setFetchingSeg] = useState(false);
  const debounceTimerRef = useRef(null);
  
  useEffect(() => {
    if(tutorial === 1) {
        setSegment(9153);
    } else if (tutorial === 3) {
      setFilters([[{
        days: 90,
        days_operator: "$gt",
        filter_type: "spend",
        value: 50,
        value_operator: "$gt"
      }]]);
    } else if (tutorial === 6) {
      setFilters([[{
        days: 90,
        days_operator: "$gt",
        filter_type: "spend",
        value: 50,
        value_operator: "$gt"
      }, {
        filter_type: 'orders',
        days_operator: "any",
        value: "5",
        value_operator: "$gt"
      }]]);
      setSegment(16722);
    } else if (tutorial === 8 || tutorial === 9) {
      setFilters([
        [{
          days: 90,
          days_operator: "$gt",
          filter_type: "spend",
          value: 50,
          value_operator: "$gt"
        }, 
        {
          filter_type: 'orders',
          days_operator: "any",
          value: "5",
          value_operator: "$gt"
        }],
        [{
          filter_type: "account",
          value: "1"
        }]
      ]);
      setSegment(10281);
    }
  }, [tutorial])

  const getSegment = async () => {
    if (tutorial === 0) {
      let emptyFilters = false;
      for (let and of filters) {
        for (let or of and) {
          if (or.filter_type === '') {
            emptyFilters = true;
            break;
          }
        }
        if (emptyFilters) break;
      }
      if (!emptyFilters) {
        try {
          const token = await getJwtToken();
          const res = await axios.post(
            'https://api.amplifyloyalty.com/app/customers/segment', 
            { filters, store: giveaway.store },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.data.name === 'BadRequestError') {
            handleSignOut();
          } else {
            setSegment(res.data);
            // save a copy of filters so we know they've been processed
            setPrevFilters(JSON.parse(JSON.stringify(filters)));
            setFetchingSeg(false);
          }
        } catch (err) {
          console.log(err);
          setFetchingSeg(false);
        }
      } else {
        setFetchingSeg(false);
      }
    } 
  };

  const immediateSegmentUpdate = () => {
    if(tutorial === 0) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (!_.isEqual(filters, prevFilters) && !errors) {
        setFetchingSeg(true);
        getSegment();
      } else {
        setFetchingSeg(false);
      }
    }
  };

  useEffect(() => {
    if (tutorial === 0) {
      setFetchingSeg(true);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        if (!_.isEqual(filters, prevFilters) && !errors) {
          getSegment();
        } else {
          setFetchingSeg(false);
        }
        debounceTimerRef.current = null;
      }, 1000);
      
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }
  }, [filters]);

  const validateErrors = async () => {
    let isError = false;
    for await (let and of filters) {
      for await (let or of and) {
        if (or.errors) {
          isError = true;
          break;
        }
      }
    }
    if (isError) {
      setErrors(true);
      return true;
    }
    else {
      setErrors(false);
      return false;
    }
  }

  const save = async () => {
    if (tutorial > 0) {
      navigate(`/app/giveaways/tutorial/rewards`);
    } else {
      const buttons = document.querySelectorAll(".update-giveaway__buttons-wrapper > *");
      // validate
      let newFilters = [...filters];
      for (let aI = 0; aI < newFilters.length; aI++) {
        newFilters[aI] = newFilters[aI].filter(or => or.filter_type !== '')
        if (newFilters[aI].length === 0) newFilters.splice(aI, 1);
      }
      setFilters(newFilters);
          
      let isError = await validateErrors();
      if (!isError) {
        buttons.forEach(btn => btn.disabled = true);
        try {
          const token = await getJwtToken();
          const res = await axios.put('https://api.amplifyloyalty.com/app/stores/giveaways',
            {
              _id: id,
              name: dynamicName,
              filters: newFilters
            },
            {
              headers: { Authorization: `Bearer ${token}` },
              params: {
                section: 'info'
              }
            }
          );
          if (res.data.name === 'BadRequestError') {
            handleSignOut();
          } else {
            setGiveaway(res.data);
            navigate(`/app/giveaways/${id}/rewards`);
            // responsehandler
          }
        } catch (err) {
          console.log(err);
          // responsehandler
        } finally {
          buttons.forEach(btn => btn.disabled = false);
        }
  
      }
    }
  }
  
  const addFilter = () => {
    setFilters([...filters, [{filter_type: ''}]]);
  }

  const addOrFilter = (i) => {
    const newFilters = [...filters];
    newFilters[i].push({filter_type: ''});
    setFilters(newFilters);
  }

  const setFilterType = (value, key, objI, filI) => {
    const newFilters = [...filters]
    let validatedValue = value;
    if (key === 'days'){
      if (validatedValue) {
        if (validatedValue && /[0-9]+/g.exec(validatedValue)) validatedValue = parseInt(/[0-9]+/g.exec(validatedValue)[0]);
        else validatedValue = 7;
      }
    }
    newFilters[filI][objI][key] = validatedValue;
    // remove errors
    if (newFilters[filI][objI].errors) {
      let daysError = newFilters[filI][objI].errors.filter(err => err.field === 'days');
      if (key === 'days_operator' && daysError.length > 0) {
        if (newFilters[filI][objI].errors.length > 1) {
          newFilters[filI][objI].errors = newFilters[filI][objI].errors.filter(err => err.field !== 'days');
        } else delete newFilters[filI][objI].errors;
      }
    }
    // validate
    if (key === 'filter_type' && value !== 'account') {
      newFilters[filI][objI].value_operator = '$gt'
      newFilters[filI][objI].value = 0;
      newFilters[filI][objI].days_operator = 'any'
    } else if (value === 'account') {
      newFilters[filI][objI].value = '1'
    } else if (key === 'days_operator' && value !== 'any') {
      newFilters[filI][objI].days = 7;
    } 
    setFilters(newFilters);
  }

  const removeFilter = (i, cI) => {
    const newFilters = [...filters];
    newFilters[i].splice(cI, 1);
    if (newFilters[i].length === 0) {
      newFilters.splice(i, 1);
    }
    setFilters(newFilters);
  }

  const setError = (message, field, i, cI) => {
    setErrors(true);
    const newFilters = [...filters];
    if (newFilters[i][cI].errors) {
      if (newFilters[i][cI].errors.filter(err => err.field === field).length === 0)
        newFilters[i][cI].errors.push({field, message});
    } else newFilters[i][cI].errors = [{field, message}];
    setFilters(newFilters);
  }
  const removeError = (i, cI, field) => {
    const newFilters = [...filters];
    let newErrors = newFilters[i][cI].errors.filter(err => err.field !== field);
    if (newErrors.length === 0) delete newFilters[i][cI].errors
    else newFilters[i][cI].errors = newErrors;
    setFilters(newFilters);
    validateErrors();
  }

  return (
    <section className="update-giveaway centralize">
      <div className="update-giveaway__options block-section" style={tutorial > 0 ? {overflow: "visible"} : {}}>
        <div className="update-giveaway__info">
          <div className="update-giveaway__row" style={{position: "relative"}}>
            <div className="update-giveaway__label">Name</div>
            <input 
              className="update-giveaway__field" 
              defaultValue={dynamicName} 
              name="name" 
              onChange={(e) => setDynamicName(e.target.value)} 
              style={tutorial === 1 ? 
                {
                  position: "relative", 
                  zIndex: "55", 
                  backgroundColor: "#fff",
                  boxShadow: "0 0 10px #fff",
                } : 
                {}
              }
            />
            {tutorial === 1 && 
              <ActionDescription 
                text={`Name your Giveaway for reporting purposes such as "Returning Customers".`}
                style={{width: "220px", left: "40px", top:"-95px"}}
                triangle={{bottom: "-18px", left: "calc(50% - 9px)", transform: "rotate(180deg)"}}
                tutorial={tutorial}
                setTutorial={setTutorial}
                next="Next"
              />
            }
          </div>
        </div>
        <div className="update-giveaway__row" style={{position: "relative"}}>
          {tutorial === 2 && 
            <ActionDescription 
              text={`Currently, the Giveaway is set to include every customer in your program. To create a segment, click "Add filter".`}
              style={{width: "220px", left: "110px", top:"-30px"}}
              triangle={{left: "-18px", top: "calc(50% - 9px)", transform: "rotate(-90deg)"}}
              tutorial={tutorial}
              setTutorial={setTutorial}
            />
          }
          {filters.length === 0 && 
            <div className="update-giveaway__add-filter-container"
              style={tutorial === 2 ? 
                {
                  position: "relative", 
                  zIndex: "55", 
                  backgroundColor: "#fff",
                  width: "fit-content",
                  padding: "5px",
                  borderRadius: "var(--small-border-radius)",
                  boxShadow: "0 0 10px #fff",
                } :
                {}
              }
            >
              <div 
                className="update-giveaway__add-filter" 
                onClick={() => {
                  if (tutorial === 2) setTutorial(3);
                  else addFilter();
                }}
              >+ Add filter</div>
            </div>
          }
          {filters.length > 0 && 
            <div className="update-giveaway__filters">
              <div className="update-giveaway__filter-header" style={{position: "relative"}}>
                <div className="update-giveaway__label">Filters</div>
                <div className="update-giveaway__segment-container" 
                  style={tutorial === 4 ? 
                    {
                      position: "relative", 
                      zIndex: "55", 
                      backgroundColor: "#fff",
                      boxShadow: "0 0 10px #fff",
                      borderRadius: "var(--small-border-radius)",
                    } : 
                    {}
                  }
                >
                  <div className="update-giveaway__label" style={tutorial === 4 ?
                    {margin: "0", padding: "3px"} :
                    {}
                  }>Segment Size:</div>
                  {fetchingSeg ? (
                    <Loading className="update-giveaway__segment-loading" />
                  ) : (
                    <div className="update-giveaway__segment-size" style={tutorial === 4 ?
                      {margin: "0", padding: "3px"} :
                      {}
                    }>{Intl.NumberFormat('en-US').format(segment)}</div>
                  )}
                </div>
                {tutorial === 4 && 
                  <ActionDescription 
                    text={`As you add filters, your Segment Size will update based on how many customers match your filters.`}
                    style={{width: "220px", right: "165px", bottom:"-45px"}}
                    triangle={{right: "-18px", top: "calc(50% - 9px)", transform: "rotate(90deg)"}}
                    tutorial={tutorial}
                    setTutorial={setTutorial}
                    next="Next"
                  />
                }
              </div>
              <div className="update-giveaway__filter-options" style={{position: "relative"}}>
                {tutorial === 6 && 
                  <ActionDescription 
                    text={`Now a customer will be included in this segment if they have completed either of the specified actions.`}
                    style={{width: "220px", left: "calc(50% - 120px)", top:"-130px"}}
                    triangle={{bottom: "-18px", left: "calc(50% - 9px)", transform: "rotate(180deg)"}}
                    tutorial={tutorial}
                    setTutorial={setTutorial}
                    next="Next"
                    hidePopup={true}
                  />
                }
                {(tutorial === 3 || tutorial === 6 || tutorial > 7) && <div className="popup-overlay"></div> }
                {filters.map((filterSet, i) => {
                  return (
                    <div key={i} className="update-giveaway__and-filter-container" 
                      style={(tutorial === 3 || tutorial === 6) && i === 0 ? 
                        {
                          position: "relative", 
                          zIndex: "55", 
                          backgroundColor: "#fff",
                          boxShadow: "0 0 10px #fff",
                          borderRadius: "var(--small-border-radius)"
                        } : 
                        {}
                      }
                    >
                      {tutorial === 3 && 
                        <ActionDescription 
                          text={`Choose a segment based on Customer actions with your store.`}
                          style={{width: "220px", left: "40px", top:"-115px"}}
                          triangle={{bottom: "-18px", left: "calc(50% - 9px)", transform: "rotate(180deg)"}}
                          tutorial={tutorial}
                          setTutorial={setTutorial}
                          next="Next"
                          hidePopup={true}
                        />
                      }
                      <div className="update-giveaway__filter-container" 
                        style={tutorial === 8 ? {
                          position: "relative", 
                          zIndex: "55", 
                          backgroundColor: "#fff",
                          boxShadow: "0 0 10px #fff",
                          borderRadius: "var(--small-border-radius)"
                        } : 
                        {}}
                      >
                        {filterSet.map((filter, cI) => {
                          return (
                            <div key={cI} className="update-giveaway__filter">
                              <div className="update-giveaway__filter-row" style={{position: "relative"}}>
                                <div className="update-giveaway__filter-type">
                                  <span className="update-giveaway__filter-intro">Customer has</span>
                                  <select name="filter_type" className='update-giveaway__filter-option' onChange={(e) => setFilterType(e.target.value, 'filter_type', cI, i)} value={filter.filter_type}>
                                    <option value="" disabled></option>
                                    <option value="spend">spent</option>
                                    <option value="orders">ordered</option>
                                    <option value="account">created account</option>
                                  </select>
                                </div>
                                <i className="fa-solid fa-trash update-giveaway__delete-filter" onClick={() => {
                                  if (tutorial === 0) removeFilter(i, cI)
                                }}></i>
                              </div>
                              {filter.filter_type === 'spend' && 
                                <div className="update-giveaway__dynamic-filter-options">
                                  <select name="value_operator" className='update-giveaway__filter-option' onChange={(e) => setFilterType(e.target.value, 'value_operator', cI, i)} value={filter.value_operator}>
                                    <option value="$gt">more than</option>
                                    <option value="$lt">less than</option>
                                    <option value="$eq">exactly</option>
                                    {/* add a between dates option */}
                                  </select>
                                  <div className="update-giveaway__input-with-prefix">
                                    {filter.errors && filter.errors.filter(err => err.field === 'value').length > 0 &&
                                      <div className="update-giveaway__error">{filter.errors.filter(err => err.field === 'value')[0].message}</div>
                                    }
                                    <div className="update-giveaway__input-prefix-wrapper">
                                      <span className="update-giveaway__input-prefix">$</span>
                                    </div>
                                    <input
                                      type="number"
                                      name="value"
                                      value={filter.value}
                                      onKeyUp={() => {
                                        if (filter.value !== 0 && !filter.value)
                                          setError("Can't be null", 'value', i, cI);
                                        else if (filter.errors)
                                          removeError(i, cI, 'value');
                                      }}
                                      className='update-giveaway__filter-option update-giveaway__filter-option--input update-giveaway__filter-option--prefixed'
                                      onChange={(e) => setFilterType(parseFloat(e.target.value), 'value', cI, i)}
                                      onBlur={immediateSegmentUpdate}
                                    />
                                  </div>
                                  <select name="days_operator" value={filter.days_operator} className='update-giveaway__filter-option' onChange={(e) => setFilterType(e.target.value, 'days_operator', cI, i)}>
                                    <option value="$gt">in the last</option>
                                    <option value="$lt">at least</option>
                                    <option value="$eq">exactly</option>
                                    <option value="any">of all time</option>
                                  </select>
                                  {filter.days_operator !== 'any' &&
                                    <div className="update-giveaway__conditional-fields">
                                      {filter.errors && filter.errors.filter(err => err.field === 'days').length > 0 &&
                                        <div className="update-giveaway__error">{filter.errors.filter(err => err.field === 'days')[0].message}</div>
                                      }
                                      <input 
                                        type="number" 
                                        name="days" 
                                        value={filter.days} 
                                        onKeyUp={() => {
                                          if (!filter.days) setError("Must be a positive number", 'days', i, cI);
                                          else if (filter.errors) removeError(i, cI, 'days');
                                        }} 
                                        className='update-giveaway__filter-option update-giveaway__filter-option--input' 
                                        onChange={(e) => setFilterType(parseInt(e.target.value), 'days', cI, i)}
                                        onBlur={immediateSegmentUpdate}
                                      />

                                      days{(filter.days_operator && filter.days_operator !== '$gt') ? ' ago' : ''}
                                    </div>
                                  }
                                </div>
                              }
                              {filter.filter_type === 'orders' && 
                                <div className="update-giveaway__dynamic-filter-options">
                                  <select name="value_operator" value={filter.value_operator} className='update-giveaway__filter-option' onChange={(e) => setFilterType(e.target.value, 'value_operator', cI, i)}>
                                    <option value="$gt">more than</option>
                                    <option value="$lt">less than</option>
                                    <option value="$eq">exactly</option>
                                    {/* add a between dates option */}
                                  </select>
                                  {filter.value_operator !== 'any' &&
                                  <div className="update-giveaway__conditional-fields">
                                    {filter.errors && filter.errors.filter(err => err.field === 'value').length > 0 &&
                                      <div className="update-giveaway__error">{filter.errors.filter(err => err.field === 'value')[0].message}</div>
                                    }
                                    <input 
                                      type="number" 
                                      name="value" 
                                      value={filter.value} 
                                      onKeyUp={() => {
                                        if (filter.value !== 0 && !filter.value) 
                                          setError("Can't be null", 'value', i, cI);
                                        else if (filter.errors) 
                                          removeError(i, cI, 'value');
                                      }} 
                                      className="update-giveaway__filter-option update-giveaway__filter-option--input" 
                                      onChange={(e) => setFilterType(e.target.value, 'value', cI, i)}
                                      onBlur={immediateSegmentUpdate}
                                    />

                                    <span className="update-giveaway__floating-text">times</span>
                                    <select name="days_operator" value={filter.days_operator} className='update-giveaway__filter-option' onChange={(e) => setFilterType(e.target.value, 'days_operator', cI, i)}>
                                      <option value="$gt">in the last</option>
                                      <option value="$lt">at least</option>
                                      <option value="$eq">exactly</option>
                                      <option value="any">of all time</option>
                                    </select>
                                    {filter.days_operator !== 'any' &&
                                      <div className="update-giveaway__conditional-fields">
                                        {filter.errors && filter.errors.filter(err => err.field === 'days').length > 0 &&
                                          <div className="update-giveaway__error">{filter.errors.filter(err => err.field === 'days')[0].message}</div>
                                        }
                                        <input 
                                          type="number" 
                                          name="days" 
                                          value={filter.days} 
                                          onKeyUp={() => {
                                            if (!filter.days) setError("Must be a positive number", 'days', i, cI);
                                            else if (filter.errors) removeError(i, cI, 'days');
                                          }} 
                                          className="update-giveaway__filter-option update-giveaway__filter-option--input" 
                                          onChange={(e) => setFilterType(e.target.value, 'days', cI, i)}
                                          onBlur={immediateSegmentUpdate}
                                        />

                                        days{(filter.days_operator !== '$gt') ? ' ago' : ''}
                                      </div>
                                    }
                                  </div>
                                  }
                                </div>
                              }
                              {filters[i][cI].filter_type === 'account' && 
                                <div className="update-giveaway__dynamic-filter-options update-giveaway__filter-row update-giveaway__filter-row--regular">
                                  <select name="value" className="update-giveaway__filter-option" onChange={(e) => setFilterType(e.target.value, 'value', cI, i)}>
                                    <option value="1">is true</option>
                                    <option value="0">is false</option>
                                  </select>
                                  {/* {filters[i][cI].value === 'true' &&
                                    <div className="update-giveaway__filter-row update-giveaway__filter-row--regular">
                                      <select name="days_operator" defaultValue=">" className='update-giveaway__filter-option' onChange={(e) => setFilterType(e.target.value, 'days_operator', cI, i)}>
                                        
                                        <option value=">">at least</option>
                                        <option value="<">in the last</option>
                                        <option value="=">exactly</option>
                                      </select>
                                      <input type="number" name="days" className='update-giveaway__filter-option' onChange={(e) => setFilterType(e.target.value, 'days', cI, i)}/>
                                    days ago
                                    </div>
                                  } */}
                                </div>
                              }
                              {filterSet.length > cI+1 &&
                                <div className="update-giveaway__or-divider">
                                  <div className="update-giveaway__or-divider-line"></div>
                                  <div className="update-giveaway__or-badge"><span className="update-giveaway__or-badge-text">OR</span></div>
                                  <div className="update-giveaway__or-divider-line"></div>
                                </div>
                              }
                            </div>
                          )
                        })}
                        <div 
                          className="update-giveaway__add-or-filter" 
                          onClick={() => {
                            if (tutorial === 5) setTutorial(6);
                            else if (tutorial > 0) return;
                            else addOrFilter(i);
                          }}
                          style={tutorial === 5 ? 
                            {
                              position: "relative", 
                              zIndex: "55", 
                              backgroundColor: "#fff",
                              boxShadow: "0 0 10px #fff",
                            } : 
                            {}
                          }
                        ><span className="update-giveaway__or-plus-icon">+</span> OR</div>
                        {tutorial === 5 && 
                          <ActionDescription 
                            text={`Add an OR condition to increase the size of your segment.`}
                            style={{width: "180px", left: "75px", bottom:"32px", fontSize: "16px"}}
                            triangle={{left: "-18px", top: "calc(50% - 9px)", transform: "rotate(-90deg)"}}
                            tutorial={tutorial}
                            setTutorial={setTutorial}
                          />
                        }
                      </div>
                      {filters.length > i+1 &&
                        <>
                          <div className="update-giveaway__and-badge"
                            style={tutorial === 8 ? 
                              {margin: "50px 0"} : 
                              {}
                            }
                          >
                            <span className="update-giveaway__and-badge-text">AND</span>
                          </div>
                          {tutorial === 8 && 
                            <ActionDescription 
                              text={`Now a customer will be included in this segment if they have completed either of the top 2 specified actions AND completed the bottom action.`}
                              style={{width: "300px", left: "calc(50% - 160px)", bottom:"183px", fontSize: "16px"}}
                              triangle={{left: "-18px", top: "calc(50% - 9px)", display: "none"}}
                              tutorial={tutorial}
                              setTutorial={setTutorial}
                              hidePopup={true}
                              next={"Next"}
                            />
                          }
                        </>
                      }
                    </div>
                  )
                })}
                <div 
                  className="update-giveaway__add-and-filter" 
                  onClick={() => {
                    if (tutorial === 7) setTutorial(8);
                    else addFilter();
                  }}
                  style={tutorial === 7 ? 
                    {
                      position: "relative", 
                      zIndex: "55", 
                      backgroundColor: "#fff",
                      boxShadow: "0 0 10px #fff",
                    } : 
                    {}
                  }
                  ><span className="update-giveaway__and-plus-icon">+</span> AND</div>
              </div>
              {tutorial === 7 &&
                <ActionDescription 
                  text={`Add an AND condition to reduce the size of your segment.`}
                  style={{width: "220px", left: "85px", bottom:"27px"}}
                  triangle={{left: "-18px", top: "calc(50% - 9px)", transform: "rotate(-90deg)"}}
                  tutorial={tutorial}
                  setTutorial={setTutorial}
                />
              }
            </div>
          }
          <div className="update-giveaway__buttons-wrapper" style={{position: "relative"}}>
            {errors && 
              <div className="update-giveaway__is-error">Fix all errors before saving.</div>
            }
            <button 
              className="update-giveaway__continue-button btn gradient" 
              onClick={save}
              style={tutorial === 9 ?
                {
                  position: "relative", 
                  zIndex: "55", 
                  backgroundColor: "#fff",
                  boxShadow: "0 0 10px #fff",
                } :
                {}
              }
            >Continue</button>
            {tutorial === 9 && 
              <ActionDescription 
                text={`Once you are satisfied with your segment, click Continue.`}
                style={{width: "220px", right: "140px", bottom:"-10px"}}
                triangle={{right: "-18px", top: "calc(50% - 9px)", transform: "rotate(90deg)"}}
                tutorial={tutorial}
                setTutorial={setTutorial}
                hidePopup={true}
              />
            }
          </div>
        </div>
      </div>
    </section>
  )
}

const GiveawaysRewardsVariableStats = (props) => {
  const {giveaway} = props;
  const [stats, setStats] = useState({});
  
  const keyMap = {
    'currency_off': '$ in Discounts',
    'percent_off': 'Avg % Discount',
    'free_shipping': 'Free Shipping',
    'points': 'Points Given'
  }
  
  useEffect(() => {
    const giveawayBreakdown = giveaway.rewards.breakdown;
    if (giveaway.rewards.reward_type === 'points') {
      setStats({points: giveawayBreakdown.reduce((acc, obj) => {
        return acc + ((!obj.value || !obj.segment) ? 0 : obj.value * obj.segment);
      }, 0).toLocaleString("en-US")})
    } else {
      let newStats = {};
      for (let breakdown of giveawayBreakdown) {
        switch (breakdown.coupon.reward_type) {
          case "percent_off":
            if (newStats.percent_off) {
              if (newStats.percent_off[`${breakdown.coupon.value}`]) {
                newStats.percent_off[`${breakdown.coupon.value}`] += breakdown.segment;
              } else {
                newStats.percent_off[`${breakdown.coupon.value}`] = breakdown.segment;
              }
            } else {
              newStats.percent_off = {}
              newStats.percent_off[`${breakdown.coupon.value}`] = breakdown.segment;
            }
            break;
          case "currency_off":
            newStats.currency_off = newStats.currency_off ? 
              newStats.currency_off + (breakdown.coupon.value * breakdown.segment) :
              breakdown.coupon.value * breakdown.segment
            break;
          case "free_shipping":
            break;
          default:
            console.log(`Could not find ${breakdown.reward_type} type.`);
        }
      } 
      setStats(newStats);
    }
  }, [giveaway]) 

  const renderAvgPct = () => {
    const { totalContributions, totalOccurrences } = Object.entries(stats.percent_off).reduce((acc, [key, value]) => {
      const percentage = parseFloat(key);
      const count = value;
      acc.totalContributions += percentage * count;
      acc.totalOccurrences += count;
      return acc;
    }, { totalContributions: 0, totalOccurrences: 0 });
    let total = totalContributions / totalOccurrences;
    return `${total ? (total).toFixed(2) : 0}%`;
  }

  return (
    <>
      {!giveaway.rewards.breakdown.length && 
        <div className="giveaway-segment-header__stat-card">
          <div className="giveaway-segment-header__stat">0</div>
          <div className="giveaway-segment-header__stat-title">{giveaway.rewards.reward_type === 'coupon' ? "$ in Discounts" : "Points Given"}</div>          
        </div>
      }
      {giveaway.rewards.breakdown.length > 0 && 
        Object.keys(stats).map(key => {
          // return false if free_shipping
          if (key === "free_shipping") return false;
          return (
            <div key={key} className="giveaway-segment-header__stat-card">
              <div className="giveaway-segment-header__stat">
                {
                  key === "points" ? 
                    stats[key]
                  : key === 'currency_off' ? 
                    Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 0}).format(stats[key]) 
                  : renderAvgPct()
                }
              </div>
              <div className="giveaway-segment-header__stat-title">{keyMap[key]}</div> 
            </div>
          )
        })
      }
    </>
  )
}

const GiveawaysRewardsHeader = (props) => {
  const {segment, giveaway, tutorial, setTutorial} = props;
  return (
    <div className="giveaway-segment-header block-section">
      {segment !== false && 
        <div className="giveaway-segment-header__stats" style={{position: "relative"}}>
          <div className="giveaway-segment-header__stat-card">
            <div className="giveaway-segment-header__stat-container"
              style={tutorial === 5 ? {
                position: "relative", 
                zIndex: "55", 
                backgroundColor: "#fff",
                boxShadow: "0 0 10px #fff",
                width: "fit-content",
                margin: "auto",
                padding: "10px",
                borderRadius: "var(--large-border-radius)"
              } : {}}
            >
              <div className="giveaway-segment-header__stat">{segment.toLocaleString("en-US")}</div>
              <div className="giveaway-segment-header__stat-title">Segment Total</div>
            </div>
          </div>
          <div className="giveaway-segment-header__stat-card">
            <div className="giveaway-segment-header__stat-container"
              style={tutorial === 6 ? {
                position: "relative", 
                zIndex: "55", 
                backgroundColor: "#fff",
                boxShadow: "0 0 10px #fff",
                width: "fit-content",
                margin: "auto",
                padding: "10px",
                borderRadius: "var(--large-border-radius)"
              } : {}}
            >
              <div className="giveaway-segment-header__stat">
                {giveaway.rewards.breakdown.reduce((acc, obj) => {
                    return acc + (!obj.segment ? 0 : obj.segment);
                  }, 0).toLocaleString('en-US')}
                </div>
              <div className="giveaway-segment-header__stat-title">Recipients</div>
            </div>
          </div>
         <GiveawaysRewardsVariableStats 
          giveaway={giveaway} 
        />
        {tutorial === 5 && 
          <ActionDescription 
            text={`Your total Recipients must not exceed your Segment Total.`}
            style={{width: "220px", left: "80px", top:"-100px", textAlign: "left"}}
            triangle={{bottom: "-18px", left: "calc(50% - 9px)", transform: "rotate(180deg)"}}
            tutorial={tutorial}
            setTutorial={setTutorial}
            next="Next"
          />
        }
        {tutorial === 6 && 
          <ActionDescription 
            text={`Recipients are chosen at random from the Segment Total. Recipients cannot be chosen twice for the same Giveaway.`}
            style={{width: "240px", right: "calc(50% - 130px)", top:"-130px", textAlign: "left"}}
            triangle={{bottom: "-18px", left: "calc(50% - 9px)", transform: "rotate(180deg)"}}
            tutorial={tutorial}
            setTutorial={setTutorial}
            next="Next"
          />
        }
        </div>
      }
    </div>
  )
}

export const GiveawaysRewards = (props) => {
  const {handleSignOut, getJwtToken} = useContext(AuthContext);
  const { giveaway, setGiveaway, tutorial, setTutorial } = props;
  const {id} = useParams();
  const navigate = useNavigate();
  const [segment, setSegment] = useState(0);
  const [error, setError] = useState(false);
  const [coupIndex, setCoupIndex] = useState(false);
  const [creatingCoupon, setCreatingCoupon] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [switchSelection, setSwitchSelection] = useState(false);

  useEffect(() => {
    const getSegment = async () => {
      try {
        const token = await getJwtToken();
        const res = await axios.post('https://api.amplifyloyalty.com/app/customers/segment',
          {
            _id: giveaway._id,
            store: giveaway.store,
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          });
        if (res.data.name === 'BadRequestError') {
          handleSignOut();
        } else {
          setSegment(res.data);
        }
      } catch (err) {
        console.log(err);
      }
    };

    if (tutorial === 0) {
      let giveawayWithKeys = giveaway;
      giveawayWithKeys.rewards.breakdown = giveawayWithKeys.rewards.breakdown.map(rew => {
        return {...rew, key: Math.random()*1000000};
      })
      setGiveaway(giveawayWithKeys);
      getSegment();
    } else {
      setSegment(10281);
    }
  }, []);

  const addGiveaway = (i) => {
    if (giveaway.rewards.reward_type === 'points') setGiveaway({...giveaway, rewards: {...giveaway.rewards, breakdown: [...giveaway.rewards.breakdown, {key: Math.random()*1000000, value: '', segment: ''}]}});
    if (giveaway.rewards.reward_type === 'coupon') {
      setCoupIndex(i);
      setCreatingCoupon(true);
      if (Object.keys(giveaway.rewards.breakdown.slice(-1)).length > 1) setGiveaway({...giveaway, rewards: {...giveaway.rewards, breakdown: [...giveaway.rewards.breakdown, {key: Math.random()*1000000, segment: ''}]}});
    }
  }

  const changeReward = (v) => {
    if (tutorial === 7) setTutorial(8);
    setGiveaway({...giveaway, rewards: {...giveaway.rewards, reward_type: v, breakdown: []}});
    setSwitchSelection(false);
  }

  const updateGiveaway = (v, i, f) => {
    const newGiveawayPoints = [...giveaway.rewards.breakdown];
    const validatedValue = /[0-9]+/g.exec(v);
    if (validatedValue) {
      newGiveawayPoints[i][f] = parseInt(/[0-9]+/g.exec(v)[0]);
    } else {
      newGiveawayPoints[i][f] = '';
    }
    setGiveaway({...giveaway, rewards: {...giveaway.rewards, breakdown: newGiveawayPoints}});
  }

  const deleteGiveaway = (i) => {
    // check for consent first
    let newGiveawayPoints = [...giveaway.rewards.breakdown];
    newGiveawayPoints.splice(i, 1);
    setGiveaway({...giveaway, rewards: {...giveaway.rewards, breakdown: newGiveawayPoints}});
    setDeleteConfirmation(false);
  }

  useEffect(() => {
    maxRecipientsValidator();
  }, [giveaway, segment])
  
  const maxRecipientsValidator = () => {
    let button = document.querySelector('.update-giveaway__continue-button');
    if (!button) return;

    let recipients = giveaway.rewards.breakdown.reduce((acc, obj) => {
      return acc + (!obj.segment ? 0 : obj.segment)
    }, 0);
    if (recipients > segment) {
      setError('Recipients larger than segment size.');
      button.disabled = true;
      return true;
    } else {
      button.disabled = false;
      setError(false);
      return false;
    }
  }

  const validate = () => {
    // check rewards.length > 0
    if (giveaway.rewards.breakdown.length === 0) {
      setError('Must have at least one reward.');
      return true;
    } else if (giveaway.rewards.reward_type === 'coupon') {
      for (let reward of giveaway.rewards.breakdown) {
        if (!parseInt(reward.segment) || reward.segment < 1) {
          setError('Recipients must be at least 1 for each reward.');
          return true;
        }
      }
    } else if (giveaway.rewards.reward_type === 'points') {
      for (let reward of giveaway.rewards.breakdown) {
        if (!parseInt(reward.segment) || reward.segment < 1 || !parseInt(reward.value) || reward.value < 1) {
          setError('Points and recipients must be at least 1 for each reward.');
          return true;
        }
      }
    }
    const recipients = maxRecipientsValidator();
    if (recipients) return true;
    setError(false);
    return false;
    // if points, check points && segment > 0
  }

  const save = async () => {
    if (!tutorial) {
      const newError = await validate();
      if (!error && !newError) {
        let buttons = document.querySelectorAll('.update-giveaway__buttons-wrapper > *');
        buttons.forEach(button => button.disabled = true);
        try {
          const token = await getJwtToken();
          const res = await axios.put('https://api.amplifyloyalty.com/app/stores/giveaways',
            {
              _id: id,
              rewards: giveaway.rewards
            },
            {
              headers: { Authorization: `Bearer ${token}` },
              params: {
                section: 'points'
              }
            }
          );
          if (res.data.name === 'BadRequestError') {
            handleSignOut();
          } else {
            setGiveaway(res.data);
            setError(false);
            navigate(`/app/giveaways/${id}/review`);
          }
        } catch (err) {
          setError('Error occurred, please try again later.');
          console.log(err);
        } finally {
          buttons.forEach(button => button.disabled = false);
        }
        if (!error) {
          return false;
        } else return true;
      } else return true;
    } else navigate(`/app/giveaways/tutorial/review`);
  }

  const submit = async (formValues) => {
    let newGiveaway = {...giveaway};
    if (typeof coupon !== 'object') newGiveaway.rewards.breakdown.push({segment: tutorial ? 1000 : '', coupon: formValues});
    else newGiveaway.rewards.breakdown[coupIndex].coupon = formValues;
    setGiveaway(newGiveaway);
    setCreatingCoupon(false);
    if (tutorial) setTutorial(tutorial+1);
}

  return (
    <section className="update-giveaway-points centralize">
      {creatingCoupon && 
        // <GiveawaysCouponForm 
        //   coupon={creatingCoupon} 
        //   setCreatingCoupon={setCreatingCoupon} 
        //   giveaway={giveaway} 
        //   setGiveaway={setGiveaway} 
        //   i={coupIndex}
        //   tutorial={tutorial}
        //   setTutorial={setTutorial}
        // />
        <CouponForm 
          // toggle the CouponForm to force submit to continue
          // add something to the final ActionDescription for giveaways
          coupon={creatingCoupon} 
          setCreatingCoupon={setCreatingCoupon} 
          submit={submit}
          tutorial={tutorial}
          setTutorial={setTutorial}
          tutorialSubmit={true}
        />
      }
      {deleteConfirmation !== false && 
        <ConfirmationPopup 
          headline='Delete Coupon Confirmation'
          description="Once you delete this coupon it will be unrecoverable."
          action={() => deleteGiveaway(deleteConfirmation)}
          setActive={setDeleteConfirmation}
        />
      }
      {switchSelection !== false && 
        <ConfirmationPopup 
          headline='Switch Giveaway Type'
          description="If you switch now, you will lose all of the rewards you have created so far."
          action={() => changeReward(switchSelection)}
          setActive={setSwitchSelection}
        />
      }
      {giveaway && 
        <GiveawaysRewardsHeader 
          segment={segment} 
          giveaway={giveaway} 
          tutorial={tutorial}
          setTutorial={setTutorial}
        />
      }
      <div className="update-giveaway-points__wrapper block-section" style={{position: "relative"}}>
        {/* <button onClick={() => console.log(giveaway)}>giveaway</button> */}
        <div className="update-giveaway-points__header-row update-giveaway-points__row">
          <div className="update-giveaway-points__type" style={{position: "relative"}}>
            <span className="update-giveaway__floating-text">Select giveaway winners and reward them with</span>
            {tutorial === 1 && 
              <select className="update-giveaway__filter-option" 
                name="reward" 
                value="points" 
                style={tutorial === 1 ? {
                  position: "relative", 
                  zIndex: "55", 
                  backgroundColor: "#fff",
                  boxShadow: "0 0 10px #fff",        
                } : {}}
              >
                <option value="points">points</option>
                <option value="coupon">coupon</option>
              </select>  
            }
            {tutorial !== 1 && 
              <select className="update-giveaway__filter-option" 
                name="reward" 
                defaultValue={giveaway.rewards.reward_type} 
                onChange={(e) => {
                  if (tutorial === 0 && giveaway.rewards.breakdown.length > 0) setSwitchSelection(e.target.value)
                  else changeReward(e.target.value)
                }}
                style={tutorial === 7 ? {
                  position: "relative", 
                  zIndex: "55", 
                  backgroundColor: "#fff",
                  boxShadow: "0 0 10px #fff",        
                } : {}}
              >
                <option value="points">points</option>
                <option value="coupon">coupon</option>
              </select>
            }
            {tutorial === 1 &&
              <ActionDescription 
                text={`Here you can select points or coupons to give away.`}
                style={{width: "220px", right: "-80px", top:"-100px"}}
                triangle={{bottom: "-18px", left: "calc(50% - 9px)", transform: "rotate(180deg)"}}
                tutorial={tutorial}
                setTutorial={setTutorial}
                next="Next"
              />
            }
            {tutorial === 7 && 
              <ActionDescription 
                text={`Select "coupons" from the dropdown menu to give coupons instead of points.`}
                style={{width: "220px", right: "-80px", top:"-90px"}}
                triangle={{bottom: "-18px", left: "calc(50% - 9px)", transform: "rotate(180deg)"}}
              />
            }
            {/* <span className="update-giveaway__floating-text">that</span>
            <select className="update-giveaway__filter-option" 
              style={{textAlign: "center"}}
              name="expiration_date" 
              defaultValue={giveaway.expires} 
              onChange={(e) => setGiveaway({...giveaway, expires: e.target.value === 'true' ? true : false})}
            >
              <option value={false}>do not expire</option>
              <option value={true}>expires</option>
            </select> */}
            {/* {giveaway.expires && 
              <>
                <span className="update-giveaway__floating-text">on</span>
                <DatePicker 
                  containerClass="giveaways-schedule-popup__field-container" 
                  inputClass="giveaways-schedule-popup__field--special"
                  dateName="date"
                  submitting={false}
                  spaceElement=".update-giveaway-review__breakdowns"
                />
                <TimePicker 
                  className="giveaways-schedule-popup__field--special" 
                  hourName="hour"
                  minName="min"
                  daytimeName="daytime"
                  submitting={false}
                  // values={}
                />
              </>
            } */}
          </div>
        </div>
        {giveaway.rewards.reward_type === 'points' && 
          <>
            {giveaway.rewards.breakdown.map((reward, i) => { 
              return (
                <div key={reward.key} className="update-giveaway-points__points-row">
                  <div className="update-giveaway-points__options-wrapper" style={{position:"relative"}}>
                    {tutorial === 3 && 
                      <ActionDescription 
                        text={`Choose the amount of points to give away.`}
                        style={{width: "220px", left: "-45px", bottom:"-100px"}}
                        triangle={{top: "-18px", left: "calc(50% - 9px)",}}
                        tutorial={tutorial}
                        setTutorial={setTutorial}
                        next="Next"
                      />
                    }
                    {tutorial === 4 && 
                      <ActionDescription 
                        text={`Choose the number of recipients to reward.`}
                        style={{width: "220px", left: "103px", bottom:"-100px"}}
                        triangle={{top: "-18px", left: "calc(50% - 9px)",}}
                        tutorial={tutorial}
                        setTutorial={setTutorial}
                        next="Next"
                      />
                    }
                    <span className="update-giveaway__floating-text">Give</span>
                    <input 
                      className="update-giveaway__filter-option update-giveaway__filter-option--input" 
                      type="number" 
                      name="value" 
                      value={reward.value} 
                      onChange={(e) => updateGiveaway(parseFloat(e.target.value), i, 'value')}
                      style={tutorial === 3 ? {
                        position: "relative", 
                        zIndex: "55", 
                        backgroundColor: "#fff",
                        boxShadow: "0 0 10px #fff",
                      } : {}}
                    />
                    <span className="update-giveaway__floating-text">points to</span>
                    <input className="update-giveaway__filter-option update-giveaway__filter-option--input" type="number" name="segment" value={reward.segment} 
                      onChange={(e) => {
                        updateGiveaway(parseFloat(e.target.value), i, 'segment');
                      }}
                      style={tutorial === 4 ? {
                        position: "relative", 
                        zIndex: "55", 
                        backgroundColor: "#fff",
                        boxShadow: "0 0 10px #fff",
                      } : {}}
                      />
                    <span className="update-giveaway__floating-text">{giveaway.rewards.breakdown_type === '%' ? "% of " : ""}recipients.</span>
                  </div>
                  <i className="fa-solid fa-trash update-giveaway__delete-filter" onClick={() => deleteGiveaway(i)}></i>
                </div>
              )
            })}
            <div className="update-giveaway-points__add-points-wrapper"
              style={tutorial === 2 ? {
                position: "relative", 
                zIndex: "55", 
                backgroundColor: "#fff",
                boxShadow: "0 0 10px #fff",
                width: "fit-content",
                borderRadius: "var(--small-border-radius)"
              } : {}}
            >
              <div 
                className="update-giveaway-points__add-points" 
                onClick={(e) => {
                  if (tutorial === 2) {
                    setTutorial(3);
                    setGiveaway({
                      ...giveaway, 
                      rewards: 
                        {
                          ...giveaway.rewards, 
                          reward_type: "points",
                          breakdown: [
                            ...giveaway.rewards.breakdown, 
                            {
                              key: Math.random()*1000000, 
                              value: 500, 
                              segment: 1000
                            }
                          ]
                        }
                      }
                    )
                  } else addGiveaway(e);
                }}
                style={tutorial === 2 ? {padding: "5px"} : {}}
              >+ More points</div>
            </div>
            {tutorial === 2 && 
              <ActionDescription 
                text={`Click to add a set of points to give away.`}
                style={{width: "220px", left: "150px", bottom:"40px"}}
                triangle={{left: "-18px", top: "calc(50% - 9px)", transform: "rotate(-90deg)"}}
              />
            }
          </>
        }
        {giveaway.rewards.reward_type === 'coupon' && 
          <>
            <div key="coupons" className="update-giveaway-points__points-container"
              style={tutorial === 18 ? {
                position: "relative", 
                zIndex: "55", 
                backgroundColor: "#fff",
                boxShadow: "0 0 10px #fff",
                borderRadius: "var(--small-border-radius)",
                width: 'fit-content',
                padding: "1px 10px 10px",
                marginTop: "10px"
              } : {}}
            >
              {tutorial === 18 && <div className="popup-overlay" style={{backgroundColor: "rgb(0, 0, 0, 0)"}}></div> }
              {giveaway.rewards.breakdown.map((reward, i) => { 
                if (reward.coupon) {
                  return (
                    <div key={reward.key} className="update-giveaway-points__points-row">
                      <div className="update-giveaway-points__options-wrapper">
                        <span className="update-giveaway__floating-text">Give</span>
                        <div className="update-giveaway__filter-option update-giveaway__filter-option--coupon" onClick={() => {
                          setCoupIndex(i);
                          setCreatingCoupon({...reward.coupon});
                        }}>
                          <div className="update-giveaway__filter-option--text">{reward.coupon.name}</div>
                        </div>
                        <span className="update-giveaway__floating-text">coupon to</span>
                        <input className="update-giveaway__filter-option update-giveaway__filter-option--input" type="number" name="segment" value={reward.segment} 
                          onChange={(e) => {
                            updateGiveaway(parseFloat(e.target.value), i, 'segment');
                          }}/>
                        <span className="update-giveaway__floating-text">recipients.</span>
                      </div>
                      <i className="fa-solid fa-trash update-giveaway__delete-filter" onClick={() => setDeleteConfirmation(i)}></i>
                    </div>
                  )
                } else return false;
              })}
              <div className="update-giveaway-points__add-points-container" 
                style={tutorial === 8 || tutorial === 17 ? {
                  position: "relative", 
                  zIndex: "55", 
                  backgroundColor: "#fff",
                  boxShadow: "0 0 10px #fff",
                  borderRadius: "var(--small-border-radius)",
                  width: 'fit-content'
                } : {}}
              >
                <div 
                  className="update-giveaway-points__add-points" 
                  onClick={() => {
                    if (tutorial === 8) {
                      setTutorial(9);
                      setCoupIndex(0);
                      setCreatingCoupon({
                        name: '$5 off',
                        reward_type: 'currency_off',
                        description: '',
                        expires: false,
                        days_available: 7,
                        minimum: false,
                        minimum_requirement: 0,
                        value: 5,
                        usage_limit: true,
                        usage_limit_value: 1,
                        shipping_threshold: false,
                        shipping_threshold_value: 0,
                        items: {
                          restriction_type: 'all_orders',
                          ids: []
                        }
                      });
                    } else if (tutorial === 17) {
                      const newGiveaway = {...giveaway};
                      newGiveaway.rewards.breakdown = [
                        ...newGiveaway.rewards.breakdown,
                        {
                          segment: 100,
                          coupon: {
                            name: '$10 off',
                            reward_type: 'currency_off',
                            description: '',
                            expires: false,
                            days_available: 7,
                            minimum: false,
                            minimum_requirement: 0,
                            value: 5,
                            usage_limit: true,
                            usage_limit_value: 1,
                            shipping_threshold: false,
                            shipping_threshold_value: 0,
                            items: {
                              restriction_type: 'all_orders',
                              ids: []
                            }
                          }
                        },
                        {
                          segment: 10,
                          coupon: {
                            name: '$25 off',
                            reward_type: 'currency_off',
                            description: '',
                            expires: false,
                            days_available: 7,
                            minimum: false,
                            minimum_requirement: 0,
                            value: 5,
                            usage_limit: true,
                            usage_limit_value: 1,
                            shipping_threshold: false,
                            shipping_threshold_value: 0,
                            items: {
                              restriction_type: 'all_orders',
                              ids: []
                            }
                          }
                        }
                      ] 
                      setGiveaway(newGiveaway);
                      setTutorial(18);
                    } else addGiveaway(giveaway.rewards.breakdown.length);
                  }}
                  style={tutorial === 8 || tutorial === 17 ? {padding: "5px"} : {}}
                >+ Add coupon</div>
              </div>
              {tutorial === 8 && 
                <ActionDescription 
                  text={`Click to add a coupon to give away.`}
                  style={{width: "220px", left: "150px", bottom:"40px"}}
                  triangle={{left: "-18px", top: "calc(50% - 9px)", transform: "rotate(-90deg)"}}
                />
              }
              {tutorial === 17 && 
                <ActionDescription 
                  text={`Create more coupons to add to the Giveaway.`}
                  style={{width: "220px", left: "150px", bottom:"40px"}}
                  triangle={{left: "-18px", top: "calc(50% - 9px)", transform: "rotate(-90deg)"}}
                />
              }
            </div>
            {tutorial === 18 && 
              <ActionDescription 
                text={`In the event of an "Enter for a chance to win" style of Giveaway, you can tier the rewards like shown.`}
                style={{width: "185px", left: "420px", top:"calc(50% - 50px)"}}
                triangle={{left: "-18px", top: "calc(50% - 9px)", transform: "rotate(-90deg)"}}
                tutorial={tutorial}
                setTutorial={setTutorial}
                next="Next"
              />
            }
          </>
        }
        <div className="update-giveaway__buttons-wrapper" style={{position: "relative"}}>
          {error && 
            <div className="update-giveaway-points__error">{error}</div>
          }
          <button className="update-giveaway__save-button btn" onClick={() => navigate(`/app/giveaways/${id}`)}>Back</button>
          <button 
            className="update-giveaway__continue-button btn gradient" 
            onClick={save}
            style={tutorial === 19 ? {
              position: "relative", 
              zIndex: "55", 
              backgroundColor: "#fff",
              boxShadow: "0 0 10px #fff",
            } : {}}
          >Continue</button>
          {tutorial === 19 && 
            <ActionDescription 
              text={`Continue to the final review of your Giveaway.`}
              style={{width: "220px", right: "140px", bottom:"-10px"}}
              triangle={{right: "-18px", top: "calc(50% - 9px)", transform: "rotate(90deg)"}}
            />
          }
        </div>
      </div>
    </section>
  )
}

export const GiveawaysCouponForm = (props) => {
  const {
    coupon, 
    setCreatingCoupon, 
    giveaway, 
    setGiveaway, 
    i, 
    disabled,
    tutorial,
    setTutorial
  } = props;
  const [valueSign, setValueSign] = useState(false);
  const [searchPreview, setSearchPreview] = useState(false);
  const [errors, setErrors] = useState(false);
  const [formValues, setFormValues] = useState(typeof coupon === 'object' ? coupon : {
    name: '',
    reward_type: 'currency_off',
    description: '',
    expires: false,
    days_available: 7,
    minimum: false,
    minimum_requirement: 0,
    value: 0,
    usage_limit: true,
    usage_limit_value: 1,
    shipping_threshold: false,
    shipping_threshold_value: 0,
    items: {
      restriction_type: 'all_orders',
      ids: []
    }
  });

  useEffect(() => {
    // Show extra fields unless Free Shipping type
    switch (formValues.reward_type) {
      case "currency_off":
        setValueSign("$");
        break;
      case "percent_off":
        setValueSign("%");
        break;
      case "free_shipping":
        setValueSign(false);
        break;
      default: 
        setValueSign("$");
    }

  }, [formValues.reward_type]);

  const validate = async () => {
    let errs = [];
    let nameRe = /[\w\d]+/g;
    if (!nameRe.exec(formValues.name)) {
      errs.push({field: 'name', message: 'Name must contain numbers or letters.'});
    }
    if (formValues.reward_type !== 'free_shipping' && formValues.value <= 0) {
      errs.push({field: 'value', message: 'Value must be greater than 0.'});
    }
    if (formValues.items.restriction_type !== 'all_orders' && formValues.items.ids.length === 0) {
      errs.push({
        field: 'restriction_type', 
        message: `You must select at least one ${formValues.items.restriction_type === 'collections' ? 'collection' : 'product'}.`
      });
    }
    if (formValues.usage_limit && (formValues.usage_limit_value <= 0 || !parseInt(formValues.usage_limit_value))) {
      errs.push({field: 'usage_limit_value', message: 'Must be a positive whole number.'});
    }
    if (formValues.expires && (formValues.days_available <= 0 || !parseInt(formValues.days_available))) {
      errs.push({field: 'expires', message: 'Must be a positive whole number.'});
    }
    if (formValues.minimum && (formValues.minimum_requirement <= 0 || !parseFloat(formValues.minimum_requirement))) {
      errs.push({field: 'minimum', message: 'Must be a positive number.'});
    }
    if (formValues.shipping_threshold && (formValues.shipping_threshold_value <= 0 || !parseFloat(formValues.shipping_threshold_value))) {
      errs.push({field: 'shipping_threshold', message: 'Must be a positive number.'});
    }
    return errs;
  }

  const submit = async () => {
    // validate formValues
    let errs = await validate();
    if (errs.length === 0) {
      let newGiveaway = {...giveaway};
      if (typeof coupon !== 'object') newGiveaway.rewards.breakdown.push({segment: '', coupon: formValues});
      else newGiveaway.rewards.breakdown[i].coupon = formValues;
      setGiveaway(newGiveaway);
      setCreatingCoupon(false);
    } else {
      console.log({errs});
      setErrors(errs);
    }
  }

  return (
    <div className="flex-popup-overlay">
      {searchPreview && 
        <SimpleSearchPreview 
          close={setSearchPreview}
          heading={formValues.items.restriction_type === 'collections' ? 'Add collections' : 'Add products'}
          formValues={[formValues,setFormValues]}
          endpoint={formValues.items.restriction_type === 'collections' ? 'collections' : 'products'}
        />
      }
      {!searchPreview && 
        <section className="redemption-form-container" style={tutorial ? {overflow: "visible"} : {}}>
          <div className="redemption-form">
            <div className="redemption-form__heading">
              <h3 className='redemption-form__title'>Coupon Reward</h3>
              <div className="redemption-form__editing">
                {/* {isEditing && <div className="redemption-form__button"><i className="fa-solid fa-trash redemption-form__delete" onClick={() => deleteRedemption()}></i></div>} */}
              </div>
            </div>
            <div className="redemption-form__fields" style={tutorial ? {overflow: "visible"} : {}}>
              {tutorial > 0 && 
                <div className="popup-overlay">
                </div>
              }
              <div className="redemption-form__field"
                style={tutorial === 9 ? 
                  {
                    position: "relative",
                    zIndex: "55",
                    backgroundColor: "#fff",
                    borderRadius: "var(--small-border-radius)",
                    boxShadow: "0 0 10px #fff",
                    paddingLeft: "5px"
                  } : 
                  {}
                }
              >
                <label htmlFor="name">Name</label>
                <div className="redemption-form__input-container">
                  <input className="redemption-form__input" disabled={disabled} name="name" value={formValues.name} onChange={(e) => setFormValues({...formValues, name: e.target.value })}/>
                </div>
                {errors && errors.filter(e => e.field === 'name').length > 0 && 
                  <div className="redemption-form__error">{errors.filter(e => e.field === 'name')[0].message}</div>
                }
                {tutorial === 9 && 
                  <ActionDescription 
                    text={`Name your coupon something the customer will understand such as "$5 off".`}
                    style={{width: "250px", right: "-285px", top: "-38px"}}
                    triangle={{left: "-18px", top: "calc(50% - 9px)", transform: "rotate(-90deg)"}}
                    hidePopup={true}
                    setTutorial={setTutorial}
                    tutorial={tutorial}
                    next="Next"
                  />
                }
              </div>
              <div className="redemption-form__field"
                style={tutorial === 10 ? 
                  {
                    position: "relative",
                    zIndex: "55",
                    backgroundColor: "#fff",
                    borderRadius: "var(--small-border-radius)",
                    boxShadow: "0 0 10px #fff",
                    paddingLeft: "5px"
                  } : 
                  {}
                }
              >
                <label htmlFor="type">Type</label>
                <div className="redemption-form__input-container">
                  <select name="reward_type" disabled={disabled} className="redemption-form__input redemption-form__input--select redemption-form__input--type" value={formValues.reward_type} onChange={(e) => setFormValues({...formValues, reward_type: e.target.value, items: { restriction_type: "all_orders", ids: []}})}>
                    <option value="currency_off">Amount off</option>
                    <option value="percent_off">Percent off</option>
                    <option value="free_shipping">Free Shipping</option>
                  </select>
                </div>
              </div>
              {valueSign && 
                <div className="redemption-form__extra-fields">
                  <div className="redemption-form__field">
                    <label htmlFor="value">Value</label>
                    <div className="redemption-form__input-container">
                      <div className="redemption-form__input-identifier redemption-form__input-identifier--value">{valueSign}</div>
                      <input className="redemption-form__input redemption-form__input--value" disabled={disabled} name="value" type="number" value={formValues.value} onChange={(e) => setFormValues({...formValues, value: parseFloat(e.target.value)})}/>
                    </div>
                    {errors && errors.filter(e => e.field === 'value').length > 0 && 
                      <div className="redemption-form__error">{errors.filter(e => e.field === 'value')[0].message}</div>
                    }
                  </div>
                  {/* <FormError className="vip-form__error-message" inputName="value" /> */}
                </div>
              }
              {formValues.reward_type !== 'free_shipping' &&
                <div className="redemption-form__field">
                  <label htmlFor="restriction_type">Applies to</label>
                  <div className="redemption-form__input-container">
                    <select name="restriction_type" disabled={disabled} className="redemption-form__input redemption-form__input--select redemption-form__input--type" value={formValues.items.restriction_type} onChange={(e) => setFormValues({...formValues, items: { restriction_type: e.target.value, ids: []}})}>
                      <option value="all_orders">All orders</option>
                      <option value="collections">Specific collections</option>
                      <option value="products">Specific products</option>
                    </select>
                  </div>
                  {errors && errors.filter(e => e.field === 'restriction_type').length > 0 && 
                    <div className="redemption-form__error">{errors.filter(e => e.field === 'restriction_type')[0].message}</div>
                  }
                </div>
              }
              {formValues.items.restriction_type !== 'all_orders' && 
                <div className="extra-fields">
                  {!disabled && <div className="vip-form__add-bonus" onClick={() => setSearchPreview(true)}>+ {formValues.items.restriction_type === 'collections' ? "Collection" : "Products"}</div>}
                  <SimplePicksDisplay formValues={formValues} setFormValues={setFormValues} disabled={disabled} />
                </div>
              }
              {formValues.reward_type === 'free_shipping' &&
                <>
                  <div className="redemption-form__field redemption-form__field--left">
                    <input className="redemption-form__input redemption-form__input--small" disabled={disabled} type="checkbox" name="shipping_threshold" value={formValues.shipping_threshold} checked={formValues.shipping_threshold} onChange={(e) => setFormValues({...formValues, shipping_threshold: e.target.checked})}/>
                    <label className="redemption-form__label--small" htmlFor="shipping_threshold">Max shipping value</label>
                  </div>
                  {formValues.shipping_threshold && 
                    <div className="extra-fields">
                      <div className="redemption-form__field">
                        <label className='redemption-form__sub-label' htmlFor="shipping_threshold_value">Up to</label>
                        <div className="redemption-form__input-container">
                          <div className="redemption-form__input-identifier redemption-form__input-identifier--value">$</div>
                          <input disabled={disabled} className="redemption-form__input redemption-form__input--value" name="shipping_threshold_value" type="number" value={formValues.shipping_threshold_value} onChange={(e) => setFormValues({...formValues, shipping_threshold_value: parseFloat(e.target.value)})}/>
                        </div>
                        {errors && errors.filter(e => e.field === 'shipping_threshold').length > 0 && 
                          <div className="redemption-form__error">{errors.filter(e => e.field === 'shipping_threshold')[0].message}</div>
                        }
                      </div>
                      <div className="error-message">
                      </div>
                    </div>
                  }
                </>
              }
              <div className="redemption-form__field redemption-form__field--left">
                <input disabled={disabled} className="redemption-form__input redemption-form__input--small" type="checkbox" name="usage_limit" value={formValues.usage_limit} checked={formValues.usage_limit} onChange={(e) => setFormValues({...formValues, usage_limit: e.target.checked})}/>
                <label className="redemption-form__label--small" htmlFor="usage_limit">Usage limit</label>
              </div>
              {formValues.usage_limit && 
                <div className="extra-fields">
                  <div className="redemption-form__field">
                    <label className='redemption-form__sub-label' htmlFor="usage_limit_value">Can be used</label>
                    <div className="redemption-form__input-container">
                      <input disabled={disabled} className="redemption-form__input redemption-form__input--points" name="usage_limit_value" type="number" value={formValues.usage_limit_value} onChange={(e) => setFormValues({...formValues, usage_limit_value: parseInt(e.target.value)})} />
                      <div className="redemption-form__input-identifier">times</div>
                    </div>
                    {errors && errors.filter(e => e.field === 'usage_limit_value').length > 0 && 
                      <div className="redemption-form__error">{errors.filter(e => e.field === 'usage_limit_value')[0].message}</div>
                    }
                  </div>
                </div>
              }
              <div className="redemption-form__field redemption-form__field--left">
                <input disabled={disabled} className="redemption-form__input redemption-form__input--small" type="checkbox" name="expires" value={formValues.expires} checked={formValues.expires} onChange={(e) => setFormValues({...formValues, expires: e.target.checked})}/>
                <label className="redemption-form__label--small" htmlFor="expires">Set expiration</label>
              </div>
              {formValues.expires && 
                <div className="extra-fields">
                  <div className="redemption-form__field">
                    <label className='redemption-form__sub-label' htmlFor="days_available">Expires after</label>
                    <div className="redemption-form__input-container">
                      <input disabled={disabled} className="redemption-form__input redemption-form__input--points" name="days_available" type="number" value={formValues.days_available} onChange={(e) => setFormValues({...formValues, days_available: parseInt(e.target.value)})}/>
                      <div className="redemption-form__input-identifier">days</div>
                    </div>
                    {errors && errors.filter(e => e.field === 'expires').length > 0 && 
                      <div className="redemption-form__error">{errors.filter(e => e.field === 'expires')[0].message}</div>
                    }
                  </div>
                </div>
              }
              <div className="redemption-form__field redemption-form__field--left">
                <input disabled={disabled} className="redemption-form__input redemption-form__input--small" type="checkbox" name="minimum" value={formValues.minimum} checked={formValues.minimum} onChange={(e) => setFormValues({...formValues, minimum: e.target.checked })}/>
                <label className="redemption-form__label--small" htmlFor="minimum">Minimum order value</label>
              </div>
              {formValues.minimum &&
                <div className="extra-fields">
                  <div className="redemption-form__field">
                    <label className='redemption-form__sub-label' htmlFor="minimum_value">Subtotal at least</label>
                    <div className="redemption-form__input-container">
                      <div className="redemption-form__input-identifier redemption-form__input-identifier--value">$</div>
                      <input disabled={disabled} className="redemption-form__input redemption-form__input--value" name="minimum_requirement" type="number" value={formValues.minimum_requirement} onChange={(e) => setFormValues({...formValues, minimum_requirement: parseFloat(e.target.value)})}/>
                    </div>
                    {errors && errors.filter(e => e.field === 'minimum').length > 0 && 
                      <div className="redemption-form__error">{errors.filter(e => e.field === 'minimum')[0].message}</div>
                    }
                  </div>
                </div>
              }
            </div>
            <div className="redemption-form__field redemption-form__button-container">
              <button className="redemption-form__cancel-button btn" type="button" onClick={() => {setCreatingCoupon(false)}}>Cancel</button>
              {!disabled && 
                <button className="redemption-form__submit-button btn gradient" type="submit" onClick={submit}>Save</button>
              }
            </div>
          </div>
        </section>
      }
    </div>
  )
}

export const GiveawaysReview = (props) => {
  const {handleSignOut, getJwtToken} = useContext(AuthContext);
  const navigate = useNavigate();
  const {giveaway, tutorial, setTutorial} = props;
  const {id} = useParams();
  const [sendType, setSendType] = useState('schedule');
  const [submitting, setSubmitting] = useState(false);
  const onLoadDate = new Date();
  onLoadDate.setMinutes(onLoadDate.getMinutes() + 10);
  const [date, setDate] = useState(onLoadDate);
  const [formValues, setFormValues] = useState({
    date: `${date.getMonth()+1}/${date.getDate()}/${date.getFullYear()}`,
    hour: `${date.getHours() > 12 ? date.getHours() - 12 : date.getHours() === 0 ? '12' : date.getHours()}`,
    min: `${date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes()}`,
    daytime: `${date.getHours() > 11 ? 'pm' : 'am'}`
  })
  const [error, setError] = useState(false);

  useEffect(() => {
    if (tutorial === 3) setSendType("send");
  }, [tutorial])

  const launch = async () => {
    if (tutorial > 0) {
      try {
        const token = await getJwtToken();
        await axios.put('https://api.amplifyloyalty.com/app/stores/onboarding', 
          {section: "giveaways"},
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        navigate('/app/giveaways');
      } catch (err) {
        console.log(err);
      }
    } else {
      const buttons = document.querySelectorAll('button');
      buttons.forEach(btn => btn.disabled = true);
      try {
        const token = await getJwtToken();
        const res = await axios.post(
          'https://api.amplifyloyalty.com/app/stores/giveaways/schedule',
          {...giveaway, type: 'execute'},
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        if (res.data.name === 'BadRequestError') {
          handleSignOut();
        } else {
          navigate('/app/giveaways');
        }
      } catch (err) {
        console.log(err);
        buttons.forEach(btn => btn.disabled = false);
      }
    }
  }

  const keyConverter = {
    $gt: 'more than',
    $lt: 'less than',
    spend: 'spent',
    account: 'created account',
    orders: 'ordered',
    any: 'of all time'
  }

  const updateValue = (name, value) => {
    setFormValues({...formValues, [name]: value});
  }

  const schedule = async () => {
    // validate that the date is not less than 5 minutes into the future
    const buttons = document.querySelectorAll('button');
    buttons.forEach(btn => btn.disabled = true);
    let dateArr = formValues.date.split('/');
    let hour = parseInt(formValues.hour);
    if (formValues.daytime === 'am' && formValues.hour === 12) {
      hour = 0;
    }
    if (formValues.daytime === 'pm' && formValues.hour !== 12) {
      hour = hour+12;
    }
    let execution_time = new Date(dateArr[2], `${dateArr[0]-1}`, dateArr[1], `${hour}`, formValues.min);
    let now = new Date();
    if (execution_time - now < 600000) {
      setError('Scheduled execution must be at least 10 minutes in the future.');
      buttons.forEach(btn => btn.disabled = false);
      return;
    } else setError(false);
    try {
      const token = await getJwtToken();
      const res = await axios.post(`https://api.amplifyloyalty.com/app/stores/giveaways/schedule`,
        {
          ...giveaway, 
          execution_time
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (res.data.name === 'BadRequestError') {
        handleSignOut();
      } else {
        navigate('/app/giveaways');
      }
    } catch (err) {
      buttons.forEach(btn => btn.disabled = false);
      console.log(err);
    }
  }

  return (
    <section className="update-giveaway-review centralize">
      <div className="update-giveaway-review__breakdowns block-section">
        <div className="update-promotion-review__title">Review to Schedule</div>
        <div className="update-giveaway-review__point-breakdown" style={{position: "relative"}}>
          {tutorial === 1 && <div className="popup-overlay" style={{backgroundColor: "rgb(0, 0, 0, 0)", zIndex: "56"}}></div>}
          {tutorial === 1 && 
            <ActionDescription 
              text={`After reviewing, click the Edit button to make changes to your Filters or Rewards.`}
              style={{width: "220px", right: "-255px", top:"-30px", zIndex: "60"}}
              triangle={{left: "-18px", top: "calc(50% - 9px)", transform: "rotate(-90deg)"}}
              tutorial={tutorial}
              setTutorial={setTutorial}
              next="Next"
            />
          }
          <div className="update-giveaway-review__breakdown-header" style={tutorial === 1 ? {
             position: "relative", 
             zIndex: "55", 
             backgroundColor: "#fff",
             boxShadow: "0 0 10px #fff",  
             padding: "5px",
             borderRadius: "var(--large-border-radius)"
          } : {}}>
            <div className="update-promotion-review__item-icon-wrapper">
              <div className="update-promotion-review__item-icon">i</div>
            </div>
            <div className="update-giveaway-review__header-right">
              <div className="update-giveaway-review__section-info">
                <h3 className="update-giveaway-review__sub-header">Filters</h3>
                {giveaway.filters.length === 0 && 
                  <div>Entire customer base selected as segment.</div>
                }
                {giveaway.filters.length > 0 && giveaway.filters.map((orArr, i) => { 
                  return (
                    <div key={i} className="update-giveaway-review__and-filters">
                      <div className="update-giveaway-review__or-filters">
                      {orArr.map((filter, oI) => 
                        <span key={oI} className="update-giveaway-review__or-filter">
                          Customer has {keyConverter[filter.filter_type]}
                          {` ${keyConverter[filter.value_operator]}`}
                          {filter.filter_type !== 'account' && ` ${filter.filter_type === 'spend' ? '$' : ''}${filter.value}${filter.filter_type === 'orders' ? ' times' : ''}`}
                          {oI+1 < orArr.length && <span className="update-giveaway-review__or-separator"> OR</span>}<br />
                        </span>
                      )}
                      </div>
                      {i+1 < giveaway.filters.length &&
                        <div className="update-giveaway-review__and-separator">
                          AND
                        </div>
                      }
                    </div>
                  
                  )
                })}
              </div>
              <a href={`/app/giveaways/${id}`}><button className="btn">Edit</button></a>
            </div>
          </div>
        </div>
        <div className="update-giveaway-review__point-breakdown">
          <div className="update-giveaway-review__breakdown-header" style={tutorial === 1 ? {
            position: "relative", 
            zIndex: "55", 
            backgroundColor: "#fff",
            boxShadow: "0 0 10px #fff",  
            padding: "5px",
            borderRadius: "var(--large-border-radius)"
          } : {}}>
            <div className="update-promotion-review__item-icon-wrapper">
              <div className="update-promotion-review__item-icon">i</div>
            </div>
            <div className="update-giveaway-review__header-right">
              <div className="update-giveaway-review__section-info">
                <h3 className="update-giveaway-review__sub-header">Rewards</h3>
                {giveaway.rewards.breakdown.map((reward, i) => {
                  return (
                    <div key={i} className="update-giveaway-review__point">
                      <strong>{reward.segment}</strong> customers get <strong>{giveaway.rewards.reward_type === 'points' ? reward.value : reward.coupon.name}</strong> {giveaway.rewards.reward_type === 'points' ? 'points' : 'coupon'}
                    </div>
                  )
                })}
              </div>
              <a href={`/app/giveaways/${id}/rewards`}><button className="btn">Edit</button></a>
            </div>
          </div>
        </div>
        {/* <div className="update-giveaway-review__point-breakdown">
          <div className="update-giveaway-review__breakdown-header">
            <h3 className="update-giveaway-review__sub-header">Totals</h3>
          </div>
          <div>
            {segment && 
              giveaway.points.breakdown_type === '#' ? 
              giveaway.points.breakdown.reduce((acc, obj) => {
                return acc + (!obj.segment ? 0 : obj.segment);
              }, 0) : 
              Math.ceil(giveaway.points.breakdown.reduce((acc, obj) => {
                return acc + (!obj.segment ? 0 : (obj.segment / 100 * segment))
              }, 0))
            } recipients out of {segment} customers.
          </div>
          <div>
            {segment && 
              giveaway.points.breakdown_type === '#' ? 
              giveaway.points.breakdown.reduce((acc, obj) => {
                return acc + (!obj.value ? 0 : obj.value);
              }, 0) : 
              Math.ceil(giveaway.points.breakdown.reduce((acc, obj) => {
                return acc + (!obj.value ? 0 : (obj.value / 100 * segment))
              }, 0))
            } in total points given.
          </div>
          <div>
            x in total dollar value of points.
          </div>
        </div> */}
        
        <div className="giveaways-schedule-popup__cards" style={{position: "relative"}}>
          {tutorial === 2 && 
            <ActionDescription 
              text={`You can schedule your Giveaway for a future date.`}
              style={{width: "200px", left: "47.5px", top:"-100px", zIndex: "60"}}
              triangle={{bottom: "-18px", left: "calc(50% - 9px)", transform: "rotate(180deg)"}}
              tutorial={tutorial}
              setTutorial={setTutorial}
              next="Next"
            />
          }
          <div 
            className={`giveaways-schedule-popup__card ${sendType === 'send' ? 'giveaways-schedule-popup__card--inactive' : ''}`} 
            onClick={() => setSendType('schedule')}
            style={tutorial === 2 ? {
              position: "relative", 
              zIndex: "55", 
              boxShadow: "0 0 10px #fff",   
            } : {}}
          >
            <h3 className="giveaways-schedule-popup__heading"><i className={`fa-solid fa-clock giveaways-schedule-popup__icon${sendType === 'schedule' ? ' giveaways-schedule-popup__icon--active' : ''}`}></i>Schedule</h3>
            <p className="giveaways-schedule-popup__description">Schedule the giveaway to send at a later time.</p>
          </div>
          {tutorial === 3 && 
            <ActionDescription 
              text={`Or you can launch your Giveaway right away.`}
              style={{width: "200px", right: "47.5px", top:"-100px", zIndex: "60"}}
              triangle={{bottom: "-18px", left: "calc(50% - 9px)", transform: "rotate(180deg)"}}
              tutorial={tutorial}
              setTutorial={setTutorial}
              next="Next"
            />
          }
          <div 
            className={`giveaways-schedule-popup__card ${sendType === 'schedule' ? 'giveaways-schedule-popup__card--inactive' : ''}`} 
            onClick={() => setSendType('send')}
            style={tutorial === 3 ? {
              position: "relative", 
              zIndex: "55", 
              boxShadow: "0 0 10px #fff",   
            } : {}}
          >
            <h3 className="giveaways-schedule-popup__heading"><i className={`fa-solid fa-rocket giveaways-schedule-popup__icon${sendType === 'send' ? ' giveaways-schedule-popup__icon--active' : ''}`}></i>Launch</h3>
            <p className="giveaways-schedule-popup__description">Send points to the chosen segment right now.</p>
          </div>
        </div>
        {sendType === 'schedule' &&
          <div className="giveaways-schedule-popup__scheduling-wrapper">
            {tutorial === 2 && <div className="popup-overlay" style={{zIndex: "56", backgroundColor: "rgb(0, 0, 0, 0)"}}></div> }
            <div className="giveaways-schedule-popup__scheduling-header">
              Launch time
            </div>
            <div className='giveaways-schedule-popup__scheduling'>
              <StoreSingleDatePicker 
                value={formValues.date}
                onChange={updateValue}
                inputClass="giveaways-schedule-popup__field--special"
                containerClass="giveaways-schedule-popup__field-container" 
                dateName="date"
                submitting={submitting}
                showCalendar={true}
                spaceElement=".update-giveaway-review__breakdowns"
                style={tutorial === 2 ? {
                  position: "relative", 
                  zIndex: "55", 
                  boxShadow: "0 0 10px #fff",  
                  backgroundColor: "#fff",
                  borderRadius: "var(--small-border-radius)",
                  height: "fit-content",
                  marginTop: "5px"
                } : {}}
              />
              <TimePicker 
                hourName="hour"
                minName="min"
                daytimeName="daytime"
                className="giveaways-schedule-popup__field--special" 
                submitting={submitting}
                onChange={updateValue}
                values={formValues}
                style={tutorial === 2 ? {
                  position: "relative", 
                  zIndex: "55", 
                  boxShadow: "0 0 10px #fff",  
                  backgroundColor: "#fff",
                  borderRadius: "var(--small-border-radius)",
                  height: "fit-content",
                  marginTop: "5px"
                } : {}}
              />
              <div className="giveaways-schedule-popup__column giveaways-schedule-popup__column--submit">
                <button onClick={schedule} className="giveaways-schedule-popup__send-button btn gradient">Schedule</button>
              </div>
            </div>
          </div>
        }
        {sendType === 'send' && 
          <div className="giveaways-schedule-popup__send-container" style={{position: "relative"}}>
            <button 
              className="giveaways-schedule-popup__send-button btn gradient" 
              onClick={launch}
              style={tutorial === 4 ? {
                position: "relative", 
                zIndex: "55", 
                boxShadow: "0 0 10px #fff",  
                backgroundColor: "#fff",
              } : {}}
            >Launch Now</button>
            {tutorial === 4 && 
              <ActionDescription 
                text={`Click Launch Now to finish this tutorial! This is a demo and will not send to your customers.`}
                style={{width: "240px", right: "-280px", top:"-18px", zIndex: "60"}}
                triangle={{left: "-18px", top: "calc(50% - 9px)", transform: "rotate(-90deg)"}}
                tutorial={tutorial}
                setTutorial={setTutorial}
              />
            }
          </div>
        }
        {error && <div className="giveaways-schedule__error">{error}</div>}

      </div>
    </section>
  )
}